import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { FormGroup, Label, Input, Button } from 'reactstrap';
import { useTenantContext } from '../../auth/useTenantContext';
import { Drawer } from '../ui/Drawer';
import { VpSelect } from '../ui/VpSelect';
import { StatusBadge } from '../StatusBadge';
import { useTenantAdminWarehouses } from '../../api/adminWarehouses';
import { useCreateMembership, useUpdateMembership, useDeleteMembership } from '../../api/adminMemberships';
import type { AdminUser, Membership, Role } from '../../api/adminUsers';

const ALL_ROLES: Role[] = ['tenant-admin', 'voice-supervisor', 'voice-operator'];

interface Props { user: AdminUser; isOpen: boolean; onClose: () => void; inline?: boolean }

export function EditMembershipsDrawer({ user, isOpen, onClose, inline }: Props) {
  const { t } = useTranslation(['user', 'common']);
  const { token, tenantCode } = useTenantContext();
  const { data: warehouses } = useTenantAdminWarehouses(token, tenantCode);

  const createMut = useCreateMembership(token, tenantCode);
  const updateMut = useUpdateMembership(token, tenantCode);
  const deleteMut = useDeleteMembership(token, tenantCode);

  const [adding, setAdding] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  const inner = (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <p style={{ fontSize: 12, color: 'var(--vp-text-2, #6b7280)', margin: 0 }}>{user.email ?? '—'}</p>

        <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: 6 }}>
          {user.memberships.map((m) => (
            <li key={m.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: 6, border: '1px solid var(--vp-border, #e5e7eb)', borderRadius: 6 }}>
              {editingId === m.id ? (
                <MembershipEditForm
                  membership={m}
                  warehouseOptions={warehouses?.data ?? []}
                  onCancel={() => setEditingId(null)}
                  onSubmit={async (patch) => {
                    setError(null);
                    try {
                      await updateMut.mutateAsync({ id: m.id, patch });
                      setEditingId(null);
                    } catch (err: unknown) {
                      const e = err as { body?: { code?: string; detail?: string;
                        active_missions?: number; active_sessions?: number }; message?: string };
                      if (e?.body?.code === 'operator_has_active_work') {
                        setError(activeWorkMessage(e.body, t));
                      } else if (e?.body?.code === 'voice_operator_membership_invariant') {
                        setError(t('user:memberships.errorOperatorSingleMembership'));
                      } else {
                        setError(e?.body?.detail ?? e?.message ?? t('user:memberships.errorGeneric'));
                      }
                    }
                  }}
                />
              ) : (
                <>
                  <StatusBadge tone="info">
                    {t(`user:roles.${m.role}`, { defaultValue: m.role })}{m.warehouse_code ? ` @ ${m.warehouse_code}` : ''}
                  </StatusBadge>
                  <span style={{ flex: 1 }} />
                  <Button type="button" color="light" size="sm" onClick={() => setEditingId(m.id)}>
                    {t('common:actions.edit')}
                  </Button>
                  <Button
                    type="button" color="light" size="sm"
                    onClick={async () => {
                      const roleLabel = t(`user:roles.${m.role}`, { defaultValue: m.role });
                      const label = roleLabel + (m.warehouse_code ? ` @ ${m.warehouse_code}` : '');
                      if (!window.confirm(t('user:memberships.confirmRemove', { label }))) return;
                      setError(null);
                      try { await deleteMut.mutateAsync(m.id); }
                      catch (err: unknown) {
                        const e = err as { body?: { code?: string; detail?: string;
                          active_missions?: number; active_sessions?: number }; message?: string };
                        setError(e?.body?.code === 'operator_has_active_work'
                          ? activeWorkMessage(e.body, t)
                          : e?.body?.detail ?? e?.message ?? t('user:memberships.errorGeneric'));
                      }
                    }}>
                    {t('user:memberships.removeButton')}
                  </Button>
                </>
              )}
            </li>
          ))}
        </ul>

        {adding ? (
          <MembershipAddForm
            warehouseOptions={warehouses?.data ?? []}
            onCancel={() => setAdding(false)}
            onSubmit={async (b) => {
              setError(null);
              try {
                await createMut.mutateAsync({ body: { user_id: user.id, ...b }, idempotencyKey: crypto.randomUUID() });
                setAdding(false);
              } catch (err: unknown) {
                const e = err as { body?: { code?: string; detail?: string }; message?: string };
                if (e?.body?.code === 'voice_operator_membership_invariant') {
                  setError(t('user:memberships.errorOperatorSingleMembership'));
                } else {
                  setError(e?.body?.detail ?? e?.message ?? t('user:memberships.errorGeneric'));
                }
              }
            }}
          />
        ) : (
          <Button type="button" color="light" size="sm" onClick={() => setAdding(true)}>
            {t('user:memberships.addButton')}
          </Button>
        )}

        {error && <p role="alert" className="text-danger small mb-0">{error}</p>}

        <div className="d-flex justify-content-end">
          <Button type="button" color="primary" onClick={onClose}>{t('common:actions.close')}</Button>
        </div>
      </div>
  );
  const userLabel = user.display_name ?? user.email ?? t('user:page.fallbackLabel', { id: user.id });
  return inline ? inner : (
    <Drawer isOpen={isOpen} onClose={onClose} title={t('user:memberships.drawerTitle', { label: userLabel })}>{inner}</Drawer>
  );
}

interface AddProps {
  warehouseOptions: { id: number; code: string; name: string }[];
  onCancel: () => void;
  onSubmit: (body: { role: Role; warehouse_id?: number | null }) => void | Promise<void>;
}
function MembershipAddForm({ warehouseOptions, onCancel, onSubmit }: AddProps) {
  const { t } = useTranslation(['user', 'common']);
  const [role, setRole] = useState<Role>('voice-supervisor');
  const [warehouseId, setWarehouseId] = useState<number | null>(null);
  const needsWarehouse = role !== 'tenant-admin';
  const submit = (e: FormEvent) => {
    e.preventDefault();
    onSubmit({ role, warehouse_id: needsWarehouse ? warehouseId : null });
  };
  return (
    <form onSubmit={submit} className="d-flex flex-column gap-2 p-2 border border-dashed rounded">
      <FormGroup tag="fieldset" className="mb-0">
        <legend className="fs-13 fw-medium text-muted mb-1">{t('user:memberships.roleLabel')}</legend>
        {ALL_ROLES.map((r) => (
          <FormGroup check inline key={r}>
            <Input type="radio" name="add-role" id={`add-role-${r}`} checked={role === r} onChange={() => setRole(r)} aria-label={r} />
            <Label check for={`add-role-${r}`}>{t(`user:roles.${r}`, { defaultValue: r })}</Label>
          </FormGroup>
        ))}
      </FormGroup>
      {needsWarehouse && (
        <label className="d-flex flex-column gap-1">
          <span className="form-label mb-0">{t('user:memberships.warehouseLabel')}</span>
          <VpSelect
            ariaLabel={t('user:memberships.warehouseAria')}
            options={warehouseOptions.map((w) => ({ value: w.id, label: `${w.code} · ${w.name}` }))}
            value={warehouseId}
            onChange={(v) => setWarehouseId(v === null ? null : Number(v))}
            placeholder={t('user:memberships.warehousePlaceholder')}
          />
        </label>
      )}
      <div className="d-flex justify-content-end gap-2">
        <Button type="button" color="light" size="sm" onClick={onCancel}>{t('common:actions.cancel')}</Button>
        <Button type="submit" color="primary" size="sm">{t('user:memberships.submitNew')}</Button>
      </div>
    </form>
  );
}

interface EditProps {
  membership: Membership;
  warehouseOptions: { id: number; code: string; name: string }[];
  onCancel: () => void;
  onSubmit: (patch: { role?: Role; warehouse_id?: number | null }) => void | Promise<void>;
}
function MembershipEditForm({ membership, warehouseOptions, onCancel, onSubmit }: EditProps) {
  const { t } = useTranslation(['user', 'common']);
  const [role, setRole] = useState<Role>(membership.role);
  const [warehouseId, setWarehouseId] = useState<number | null>(membership.warehouse_id);
  const needsWarehouse = role !== 'tenant-admin';
  const submit = (e: FormEvent) => {
    e.preventDefault();
    onSubmit({ role, warehouse_id: needsWarehouse ? warehouseId : null });
  };
  return (
    <form onSubmit={submit} style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 1 }}>
      <div style={{ minWidth: 150 }}>
        <VpSelect
          ariaLabel={t('user:memberships.roleAria')}
          options={ALL_ROLES.map((r) => ({ value: r, label: t(`user:roles.${r}`, { defaultValue: r }) }))}
          value={role}
          onChange={(v) => setRole(v as Role)}
        />
      </div>
      {needsWarehouse && (
        <div style={{ minWidth: 150 }}>
          <VpSelect
            ariaLabel={t('user:memberships.warehouseAria')}
            options={warehouseOptions.map((w) => ({ value: w.id, label: w.code }))}
            value={warehouseId}
            onChange={(v) => setWarehouseId(v === null ? null : Number(v))}
            placeholder={t('user:memberships.warehousePlaceholder')}
          />
        </div>
      )}
      <Button type="submit" color="primary" size="sm">{t('common:actions.save')}</Button>
      <Button type="button" color="light" size="sm" onClick={onCancel}>{t('common:actions.cancel')}</Button>
    </form>
  );
}

/**
 * Compone il messaggio dai due conteggi separati, OMETTENDO la parte a zero:
 * "2 missioni in corso" è azionabile, "2 missioni e 0 sessioni" è rumore.
 * I plurali passano da i18next, quindi "1 missione" e non "1 missioni".
 */
function activeWorkMessage(
  body: { active_missions?: number; active_sessions?: number } | undefined,
  t: (k: string, o?: Record<string, unknown>) => string,
): string {
  const parts: string[] = [];
  const m = body?.active_missions ?? 0;
  const s = body?.active_sessions ?? 0;
  if (m > 0) parts.push(t('user:memberships.activeWork.missions', { count: m }));
  if (s > 0) parts.push(t('user:memberships.activeWork.sessions', { count: s }));
  return t('user:memberships.activeWork.blocked', { what: parts.join(' e ') });
}
